/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { FormattedMessage } from 'react-intl';

import { Typography2, breakpoints, typography2 } from '@coursera/cds-core';

import _t from 'i18n!nls/skills-common';

type Props = {
  className?: string;
  total: number;
  score: number;
  title: string;
  /** Threshold value as a count (e.g., 11 out of 13 total skills needed to pass) */
  threshold?: number;
  /** When true, uses bolder typography (actionPrimary/bodyPrimary) and long score text */
  useBoldText?: boolean;
  /** When true, uses a gradient-matched color for the threshold star border to blend with the background */
  useTransparentThresholdBorderColor?: boolean;
  hideHeader?: boolean;
  size?: 'small' | 'large';
  hideThresholdBorder?: boolean;
};

const PROGRESS_BAR_SIZES = {
  small: {
    trackHeight: 4,
    trackRadius: 8,
    progressRadius: 4,
    starSize: 16,
    starBorderWidth: 3,
    starIconSize: 8,
    noScoreIndicatorSize: 10,
  },
  large: {
    trackHeight: 8,
    trackRadius: 10,
    progressRadius: 8,
    starSize: 24,
    starBorderWidth: 4,
    starIconSize: 12,
    noScoreIndicatorSize: 16,
  },
} as const;

const getThresholdMarkerTotalDiameter = (
  size: keyof typeof PROGRESS_BAR_SIZES,
  showStar: boolean,
  hideThresholdBorder: boolean
) => {
  const { starSize, starBorderWidth } = PROGRESS_BAR_SIZES[size];
  const effectiveBorderWidth = hideThresholdBorder ? 0 : starBorderWidth;

  return showStar ? starSize + effectiveBorderWidth * 2 : Math.max(effectiveBorderWidth * 4, starSize / 2);
};

/**
 * Interpolates between two hex colors based on a percentage.
 * Used to match the parent's gradient: linear-gradient(265deg, #F0F6FF -4.87%, #F9F5FF 101.7%)
 * 265deg goes roughly right-to-left, so at 0% (left) we use #F9F5FF and at 100% (right) we use #F0F6FF
 */
const interpolateGradientColor = (percent: number): string => {
  // At 0% (left side): #F9F5FF, At 100% (right side): #F0F6FF
  const startColor = { r: 0xf9, g: 0xf5, b: 0xff }; // #F9F5FF (left)
  const endColor = { r: 0xf0, g: 0xf6, b: 0xff }; // #F0F6FF (right)

  // Normalize percent (0-100) to 0-1 range
  const t = Math.min(Math.max(percent / 100, 0), 1);

  const r = Math.round(startColor.r + (endColor.r - startColor.r) * t);
  const g = Math.round(startColor.g + (endColor.g - startColor.g) * t);
  const b = Math.round(startColor.b + (endColor.b - startColor.b) * t);

  return `rgb(${r}, ${g}, ${b})`;
};

const styles = {
  container: css`
    width: 100%;
  `,
  headerContainer: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--cds-spacing-100);
    min-width: 0;
    gap: var(--cds-spacing-150);
  `,
  title: (useBoldText: boolean) => css`
    max-width: 500px;
    white-space: normal;
    word-wrap: normal;
    word-break: normal;

    ${breakpoints?.down('sm')} {
      max-width: ${useBoldText ? '400px' : '75%'};
    }

    ${breakpoints?.down('xs')} {
      max-width: ${useBoldText ? '100%' : '75%'};
    }
  `,
  scoreText: css`
    white-space: nowrap;

    ${breakpoints?.down('xs')} {
      ${typography2.subtitleMedium}
      color: var(--cds-color-neutral-primary);
    }
  `,
  progressContainer: (isPassed: boolean, size: keyof typeof PROGRESS_BAR_SIZES) => css`
    ${(() => {
      const { trackHeight, trackRadius } = PROGRESS_BAR_SIZES[size];
      return css`
        height: ${trackHeight}px;
        border-radius: ${trackRadius}px;
      `;
    })()}
    position: relative;
    width: 100%;
    background-color: ${
      isPassed ? 'var(--cds-color-emphasis-quaternary-background-weak)' : 'var(--cds-color-neutral-disabled-strong)'
    };
    display: flex;
    align-items: center;
    align-self: stretch;
    transform: scaleY(calc(1 / 1.03));
  `,
  progressBar: (widthPercent: number, size: keyof typeof PROGRESS_BAR_SIZES, endInsetPx = 0) => {
    const { trackHeight, progressRadius } = PROGRESS_BAR_SIZES[size];
    const minWidth = `${trackHeight}px`;
    let widthValue = minWidth;
    if (widthPercent > 0) {
      widthValue = endInsetPx > 0 ? `calc(${widthPercent}% - ${endInsetPx}px)` : `${widthPercent}%`;
    }
    const border = '1px solid var(--cds-color-white-0)';

    return css`
      width: ${widthValue};
      height: ${trackHeight}px;
      background:
        linear-gradient(
          0deg,
          var(--cds-color-emphasis-quaternary-background-strong) 0%,
          var(--cds-color-emphasis-quaternary-background-strong) 100%
        ),
        var(--cds-color-white-0);
      border-radius: ${progressRadius}px;
      transition: width 0.3s ease;
      border: ${widthPercent < 100 ? border : 'none'};
      box-sizing: content-box;
      min-width: ${minWidth};
      transform: scaleY(calc(1 / 1.03));
    `;
  },
  thresholdStarContainer: (
    thresholdPercent: number,
    isPassed: boolean,
    borderColor: string,
    showStar: boolean,
    size: keyof typeof PROGRESS_BAR_SIZES,
    hideThresholdBorder: boolean
  ) => css`
    ${(() => {
      const { starSize, starBorderWidth, starIconSize } = PROGRESS_BAR_SIZES[size];
      const effectiveBorderWidth = hideThresholdBorder ? 0 : starBorderWidth;
      const totalDiameter = starSize + effectiveBorderWidth * 2;
      const edgeOffset = totalDiameter / 2;
      const indicatorSize = showStar ? starSize : Math.max(effectiveBorderWidth * 2, starSize / 2);

      return css`
        left: clamp(${edgeOffset}px, ${thresholdPercent}%, calc(100% - ${edgeOffset}px));
        width: ${indicatorSize}px;
        height: ${indicatorSize}px;
        border: ${effectiveBorderWidth}px solid ${borderColor};

        svg {
          width: ${starIconSize}px;
          height: ${starIconSize}px;
        }
      `;
    })()}
    position: absolute;
    border-radius: 50%;
    background-color: ${isPassed ? 'var(--cds-color-purple-700)' : 'var(--cds-color-neutral-stroke-primary)'};
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    box-sizing: content-box;
    transform: translate(-50%) scale(calc(1 / 1.03)); /* same factor as the ancestor scale */
    transform-origin: center;

    svg {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  `,
  noScoreIndicatorContainer: (
    thresholdPercent: number,
    borderColor: string,
    size: keyof typeof PROGRESS_BAR_SIZES,
    hideThresholdBorder: boolean
  ) => css`
    width: ${PROGRESS_BAR_SIZES[size].noScoreIndicatorSize}px;
    height: ${PROGRESS_BAR_SIZES[size].noScoreIndicatorSize}px;
    border-radius: 50%;
    background-color: var(--cds-color-emphasis-neutral-background-default);
    border: ${hideThresholdBorder ? 0 : PROGRESS_BAR_SIZES[size].starBorderWidth}px solid ${borderColor};
  `,
};

const SkillProgressBarWithThresholdStar = ({
  total,
  score,
  title,
  threshold,
  useBoldText = false,
  useTransparentThresholdBorderColor = false,
  hideHeader = false,
  size = 'small',
  hideThresholdBorder = false,
  className,
}: Props) => {
  const titleVariant = useBoldText ? 'actionPrimary' : 'bodySecondary';
  const scoreVariant = useBoldText ? 'bodyPrimary' : 'bodySecondary';
  if (total === 0) {
    return null;
  }

  const safeScore = Math.min(Math.max(score, 0), total);
  const progressPercent = Math.ceil((safeScore / total) * 100) || 0;
  const hasValidThreshold = threshold !== undefined && threshold > 0;
  const thresholdPercent = hasValidThreshold ? Math.round((threshold / total) * 100) : undefined;
  const isPassed = hasValidThreshold && safeScore > 0 && safeScore >= threshold;
  const shouldInsetProgressEnd = hasValidThreshold && thresholdPercent === 100 && safeScore === total;
  const progressEndInsetPx = shouldInsetProgressEnd
    ? getThresholdMarkerTotalDiameter(size, safeScore > 0, hideThresholdBorder) / 2
    : 0;
  const borderColor =
    useTransparentThresholdBorderColor && thresholdPercent !== undefined
      ? interpolateGradientColor(thresholdPercent)
      : 'var(--cds-color-white-0)';
  const a11yScoreText = _t(
    '{score} out of {total} {count, plural, =1 {skill} other {skills}} mastered ({progressPercent}%)',
    {
      score: `${score}`,
      total: `${total}`,
      count: total,
      progressPercent,
    }
  );
  const scoreText = useBoldText ? (
    <FormattedMessage
      message={_t('{score}/{total} {count, plural, =1 {skill} other {skills}} verified')}
      score={`${score}`}
      total={`${total}`}
      count={total}
    />
  ) : (
    `${score}/${total}`
  );

  return (
    <div css={styles.container} className={className}>
      {!hideHeader && (
        <div css={styles.headerContainer}>
          <Typography2 component="h3" variant={titleVariant} css={styles.title(useBoldText)}>
            {title}
          </Typography2>
          <Typography2 component="span" variant={scoreVariant} css={styles.scoreText}>
            {scoreText}
          </Typography2>
        </div>
      )}

      <div
        css={styles.progressContainer(isPassed, size)}
        role="progressbar"
        aria-valuenow={progressPercent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={a11yScoreText}
        aria-label={a11yScoreText}
      >
        <div css={styles.progressBar(progressPercent, size, progressEndInsetPx)} />
        {hasValidThreshold && (
          <div
            css={styles.thresholdStarContainer(
              thresholdPercent!,
              isPassed,
              borderColor,
              safeScore > 0,
              size,
              hideThresholdBorder
            )}
            aria-label={
              isPassed
                ? _t('Threshold of {threshold} out of {total} reached', { threshold, total })
                : _t('Threshold at {threshold} out of {total}', { threshold, total })
            }
          >
            {safeScore > 0 && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={PROGRESS_BAR_SIZES[size].starIconSize}
                height={PROGRESS_BAR_SIZES[size].starIconSize}
                viewBox="0 0 8 8"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.82983 0.0256828C3.89484 0.00428215 3.96349 -0.00387312 4.03173 0.00170176C4.09996 0.00727668 4.16637 0.0264681 4.22701 0.0581349C4.28765 0.0898011 4.34129 0.133297 4.38474 0.18604C4.42712 0.23748 4.45895 0.296689 4.47847 0.360309L5.22696 2.66052H7.453C7.53717 2.65519 7.62147 2.67047 7.69851 2.70516C7.78125 2.74241 7.85269 2.80072 7.90564 2.87424L7.90623 2.87506C7.9452 2.92953 7.97288 2.99121 7.98764 3.05649C8.00241 3.12176 8.00396 3.18932 7.99221 3.25519C7.98046 3.32107 7.95565 3.38395 7.91922 3.44015C7.88465 3.4935 7.84027 3.53981 7.78845 3.57666L5.89118 5.02466L6.63005 7.34684C6.65946 7.42937 6.66621 7.5183 6.64949 7.60441C6.6321 7.694 6.59 7.77698 6.52791 7.84403L6.52225 7.85015L6.51626 7.85595C6.42092 7.94832 6.29321 8 6.16026 8C6.03438 8 5.9132 7.95368 5.81974 7.87035L4.00046 6.46589L2.16935 7.8838L2.16184 7.88893C2.09814 7.93249 2.02565 7.96162 1.94946 7.97428C1.87355 7.98689 1.7958 7.98285 1.72163 7.96244C1.65647 7.94463 1.59549 7.9141 1.54224 7.87263C1.48888 7.83108 1.44435 7.77936 1.41122 7.72049C1.37809 7.66162 1.35704 7.59677 1.34928 7.52972C1.34152 7.46267 1.34721 7.39474 1.36602 7.3299L1.36832 7.32196L2.10266 5.02479L0.185343 3.56582L0.176423 3.55787C0.120611 3.50811 0.0760264 3.4471 0.0456155 3.37889C0.0152043 3.31068 -0.000341321 3.23681 5.68381e-06 3.16216L4.31904e-05 3.1541L0.000505943 3.14604C0.00776191 3.01976 0.0613515 2.90055 0.15107 2.81112C0.240788 2.72168 0.360369 2.66825 0.487042 2.66102L0.495833 2.66052H2.7433L3.50784 0.334524L3.51163 0.325132C3.53954 0.255989 3.582 0.193613 3.63614 0.14221C3.69028 0.0908068 3.75485 0.0515716 3.82549 0.027148L3.82983 0.0256828ZM1.72163 7.96244L1.72079 7.96221L1.80315 7.66665L1.72163 7.96244Z"
                  fill="white"
                />
              </svg>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillProgressBarWithThresholdStar;
